import {motion} from 'framer-motion';
import {Box} from 'platform/foundation';
import {css} from 'styled-components';

import {useRef} from 'react';

import {buildArray, RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {LightboxAction} from '../types/LightboxAction';
import {LightboxImage} from '../types/LightboxImage';
import {ZoomRef} from '../types/ZoomRef';
import {Actions} from './Actions';
import {Zoom} from './Zoom';
import {ZoomImage} from './ZoomImage';

interface LightboxSlideDetailProps extends RequiredTestIdProps {
  image: LightboxImage;
  onDetailClose: () => void;
}

export function Detail(props: LightboxSlideDetailProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ZoomRef>(null);

  useOnMount(() => {
    if (wrapperRef.current) {
      wrapperRef.current.focus(); // Focus wrapper, so user can focus the buttons by pressing tab
    }
  });

  const onZoomIn = () => {
    if (zoomRef.current) {
      zoomRef.current.zoomIn();
    }
  };

  const onZoomOut = () => {
    if (zoomRef.current) {
      zoomRef.current.zoomOut();
    }
  };

  const actions = props.image.actions ?? [];

  const zoomControl = buildArray<LightboxAction>([])
    .add({
      id: 'zoomOutBtn',
      icon: 'lightbox/minus',
      onClick: () => onZoomOut(),
    })
    .add({
      id: 'zoomInBtn',
      icon: 'lightbox/plus',
      onClick: () => onZoomIn(),
    });

  return (
    <motion.div
      css={css`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${({theme}) => theme.colors.palettes.black[10][100]};
        z-index: ${({theme}) => theme.zIndices.LIGHTBOX_DETAIL};
      `}
      initial={{opacity: 0, scale: 0.95}}
      animate={{opacity: 1, scale: 1}}
      exit={{opacity: 0, scale: 0.95}}
      tabIndex={-1} // Focus programmatically only
      ref={wrapperRef}
    >
      <Box width="100%" height="100%" position="relative">
        <Zoom
          ref={zoomRef}
          data-testid={suffixTestId('imageDetail', props)}
          renderSlide={(ref) => (
            <ZoomImage
              ref={ref}
              data-testid={suffixTestId('imageDetail', props)}
              image={props.image}
            />
          )}
        />
        <Actions
          data-testid={suffixTestId('imageDetail', props)}
          zIndex="LIGHTBOX_DETAIL_ACTIONS"
          actionPosition={{
            top: 4,
            right: 15,
            left: 'auto',
          }}
          actions={[actions, zoomControl]}
          isActive
        />
        <Actions
          data-testid={suffixTestId('imageDetail', props)}
          actionPosition={{top: 4, right: 4, left: 'auto'}}
          zIndex="LIGHTBOX_DETAIL_ACTIONS"
          actions={buildArray<LightboxAction[]>().add([
            {
              icon: 'navigation/close',
              onClick: props.onDetailClose,
              id: 'closeBtn',
            },
          ])}
          isActive
        />
      </Box>
    </motion.div>
  );
}
